
import { useState, useEffect } from 'react';
import SpinWheel from './components/SpinWheel';
import NameInput from './components/NameInput';
import ResultModal from './components/ResultModal';
import DrinkWheel from './components/DrinkWheel';
import FinalResultModal from './components/FinalResultModal';
import SpinHistory, { SpinHistoryItem } from './components/SpinHistory';
import Confetti from './components/Confetti';
import { useSound } from './hooks/useSound';

interface Participant {
  name: string;
  emoji: string;
}

export default function Spinner() {
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [isSpinning, setIsSpinning] = useState(false);
  const [winner, setWinner] = useState<string>('');
  const [showResult, setShowResult] = useState(false);
  const [showDrinkWheel, setShowDrinkWheel] = useState(false);
  const [showFinalResult, setShowFinalResult] = useState(false);
  const [selectedDrink, setSelectedDrink] = useState<string>('');
  const [selectedDrinkEmoji, setSelectedDrinkEmoji] = useState<string>('');
  const [currentSpinIndex, setCurrentSpinIndex] = useState<number | null>(null);
  const [spinHistory, setSpinHistory] = useState<SpinHistoryItem[]>(() => {
    const saved = localStorage.getItem('spinHistory');
    return saved ? JSON.parse(saved) : [];
  });
  const [showConfetti, setShowConfetti] = useState(false);
  const { playSpinSound, playWinSound } = useSound();

  useEffect(() => {
    localStorage.setItem('spinHistory', JSON.stringify(spinHistory));
  }, [spinHistory]);

  const handleAddParticipants = (newParticipants: Participant[]) => {
    setParticipants(newParticipants);
  };

  const handleSpin = () => {
    if (participants.length < 2) return;
    setIsSpinning(true);
    setShowResult(false);
    setShowConfetti(false);
    playSpinSound();
  };

  const handleSpinComplete = (selectedEmoji: string) => {
    // Find the participant by emoji
    const winningParticipant = participants.find(p => p.emoji === selectedEmoji);
    const winnerName = winningParticipant?.name || selectedEmoji;
    
    setWinner(winnerName);
    setIsSpinning(false);
    setShowResult(true);
    setShowConfetti(true);
    
    // Add new entry with no drink yet
    const newEntry: SpinHistoryItem = {
      person: winnerName,
      drink: null,
      drinkEmoji: null,
      timestamp: Date.now()
    };
    setSpinHistory(prev => [newEntry, ...prev.slice(0, 19)]);
    setCurrentSpinIndex(0);
    
    playWinSound();
  };

  const handleSpinAgain = () => {
    setShowResult(false);
    setWinner('');
    setShowConfetti(false);
    setCurrentSpinIndex(null);
  };

  const handlePickDrink = () => {
    setShowResult(false);
    setShowDrinkWheel(true);
  };

  const handleDrinkResult = (drink: string, emoji: string) => {
    setSelectedDrink(drink);
    setSelectedDrinkEmoji(emoji);
    setShowDrinkWheel(false);
    setShowFinalResult(true);
    setShowConfetti(true);
    
    // Update the current spin entry with drink info
    if (currentSpinIndex !== null) {
      setSpinHistory(prev => prev.map((item, index) => 
        index === currentSpinIndex 
          ? { ...item, drink, drinkEmoji: emoji }
          : item
      ));
    }
    
    playWinSound();
  };

  const handleStartOver = () => {
    setWinner('');
    setSelectedDrink('');
    setSelectedDrinkEmoji('');
    setShowConfetti(false);
    setCurrentSpinIndex(null);
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex flex-col">
      <Confetti show={showConfetti} duration={4000} />
      
      {/* Hero Title */}
      <section className="pt-16 pb-8 text-center px-6">
        <div className="max-w-3xl mx-auto">
          <div className="inline-flex items-center justify-center mb-5">
            <img
              src="https://static.readdy.ai/image/f9d0301ca0199288eef8b331aa5b3c77/f21a73221c1881cdc882f03693b628f9.png"
              alt="Pint Roulette"
              className="w-[120px] h-[120px] object-contain"
            />
          </div>
          <h1 className="text-5xl md:text-6xl font-extrabold text-gray-900 mb-4 leading-tight">
            Pint <span className="text-[#2349FF]">Roulette</span>
          </h1>
          <p className="text-lg text-gray-500 max-w-xl mx-auto">
            Add names and let fate decide who&apos;s buying the next round!
          </p>
        </div>
      </section>

      {/* Main Content */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-6 pb-16">
        <div className="grid lg:grid-cols-5 gap-10 items-start">
          {/* Left - Name Input */}
          <div className="lg:col-span-2 order-2 lg:order-1 space-y-6">
            <NameInput onParticipantsChange={handleAddParticipants} />
            <SpinHistory history={spinHistory} />
          </div>

          {/* Right - Wheel */}
          <div className="lg:col-span-3 order-1 lg:order-2">
            <div className="bg-white rounded-3xl shadow-lg shadow-gray-200 p-6 md:p-10">
              <SpinWheel
                names={participants.map(p => p.emoji)}
                isSpinning={isSpinning}
                onSpinComplete={handleSpinComplete}
              />

              <div className="mt-8 text-center">
                <button
                  onClick={handleSpin}
                  disabled={isSpinning || participants.length < 2}
                  className={`px-12 py-4 text-base font-bold rounded-full transition-all transform whitespace-nowrap cursor-pointer ${
                    isSpinning || participants.length < 2
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-[#2349FF] to-[#1a38cc] text-white hover:from-[#1a38cc] hover:to-[#1530b3] hover:scale-105 shadow-lg shadow-[#2349FF]/30'
                  }`}
                >
                  {isSpinning ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Spinning...
                    </>
                  ) : (
                    <>
                      <i className="ri-refresh-line mr-2"></i>
                      Spin The Wheel
                    </>
                  )}
                </button>
                {participants.length < 2 && !isSpinning && (
                  <p className="text-xs text-gray-400 mt-3">Add at least 2 names to start spinning</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      <ResultModal
        show={showResult}
        winner={winner}
        onClose={() => setShowResult(false)}
        onSpinAgain={handleSpinAgain}
        onPickDrink={handlePickDrink}
      />

      <DrinkWheel
        show={showDrinkWheel}
        winner={winner}
        onResult={handleDrinkResult}
        onBack={() => {
          setShowDrinkWheel(false);
          setShowResult(true);
        }}
        onClose={() => setShowDrinkWheel(false)}
      />

      <FinalResultModal
        show={showFinalResult}
        winner={winner}
        drink={selectedDrink}
        drinkEmoji={selectedDrinkEmoji}
        onClose={() => setShowFinalResult(false)}
        onStartOver={handleStartOver}
      />
    </div>
  );
}
